import api from "@/lib/api";
import { compressImage } from "@/lib/imageCompression";

/**
 * Parallel uploader with per-file progress + automatic retry on transient failures.
 *
 * uploadFilesInBatches({
 *   files,                          // File[]
 *   concurrency = 3,                // simultaneous in-flight uploads
 *   maxRetries  = 2,                // per-file retry count (exponential backoff)
 *   onProgress  = (id, pct) => {},  // 0..100 per file
 *   onItemDone  = (id, result) => {}// fires when ONE file finishes (success)
 *   onItemFail  = (id, err) => {},  // fires when ONE file ultimately fails
 * }) → Promise<{ ok: number, fail: number, results: [{id,file,data,error}] }>
 *
 * Each file is assigned a stable client-side id (idx) so the caller can render a list of in-flight items.
 */
export async function uploadFilesInBatches({
  files,
  concurrency = 3,
  maxRetries = 2,
  onProgress = () => {},
  onItemDone = () => {},
  onItemFail = () => {},
}) {
  const items = files.map((file, idx) => ({ id: idx, file }));
  const results = new Array(items.length);
  let ok = 0, fail = 0;
  let cursor = 0;

  const runOne = async () => {
    while (cursor < items.length) {
      const myIdx = cursor++;
      const { id, file } = items[myIdx];
      onProgress(id, 0);
      try {
        // 1) Client-side compression (downscale + JPEG re-encode if needed)
        const compressed = await compressImage(file);
        // 2) Upload with retry
        const data = await uploadWithRetry({ file: compressed, id, onProgress, maxRetries });
        ok++;
        results[myIdx] = { id, file, data };
        onProgress(id, 100);
        onItemDone(id, data);
      } catch (e) {
        fail++;
        results[myIdx] = { id, file, error: e };
        onItemFail(id, e);
      }
    }
  };

  // Spin up `concurrency` workers
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, runOne);
  await Promise.all(workers);
  return { ok, fail, results };
}

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024; // must match backend limit

async function uploadWithRetry({ file, id, onProgress, maxRetries }) {
  // Pre-flight: catch obvious client-side failures BEFORE hitting the server (avoids "Network Error" ambiguity)
  if (file.size > MAX_UPLOAD_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    const err = new Error(`File too large: ${mb} MB (max 50 MB). Compress or resize this image before uploading.`);
    err.userMessage = err.message;
    throw err;
  }
  let attempt = 0;
  let lastErr = null;
  while (attempt <= maxRetries) {
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/upload", form, {
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = Math.min(99, Math.round((e.loaded / e.total) * 100));
          onProgress(id, pct);
        },
        timeout: 180000, // 3 minutes for very large photos on slow networks
      });
      return r.data;
    } catch (e) {
      lastErr = e;
      // Enrich error with a human-readable userMessage the UI can safely surface
      e.userMessage = friendlyUploadError(e, file);
      const status = e.response?.status;
      const transient = !status || status === 408 || status === 425 || status === 429 || (status >= 500 && status < 600);
      if (!transient || attempt === maxRetries) throw e;
      const wait = 600 * Math.pow(2.5, attempt);
      await new Promise((res) => setTimeout(res, wait));
      attempt++;
    }
  }
  throw lastErr;
}

/**
 * Turn axios/network errors into an actionable message that tells the user WHY the upload failed.
 * Common cases we handle here:
 *   - Server responded with detail (413 File too large, 400 unsupported type, 401 unauthorised, etc.)
 *   - Timeout (server took >3min to respond)
 *   - Network error (no response — likely offline, DNS, or CDN blocking)
 *   - Server error (500/502/503 — origin down or storage failing)
 */
function friendlyUploadError(e, file) {
  const detail = e.response?.data?.detail;
  const status = e.response?.status;
  const sizeMB = file ? (file.size / (1024 * 1024)).toFixed(1) : "?";
  if (detail) return detail;
  if (status === 401 || status === 403) return "Your admin session has expired — please log in again.";
  if (status === 413) return `File too large (${sizeMB} MB). Max is 50 MB.`;
  if (status === 415 || status === 400) return `Unsupported file type. Use JPG, PNG, or WebP.`;
  if (status === 429) return "Too many uploads in quick succession — please wait a moment and try again.";
  if (status && status >= 500) return `Server error (${status}). Please retry in a moment.`;
  if (e.code === "ECONNABORTED" || /timeout/i.test(e.message)) {
    return `Upload timed out (file: ${sizeMB} MB). Try compressing the image or use a stronger network connection.`;
  }
  if (e.message === "Network Error" || !e.response) {
    return `Network error — check your internet connection. If you're on Wi-Fi, try 4G/5G. (File: ${sizeMB} MB)`;
  }
  return e.message || "Upload failed";
}
